
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, X, Scan, Camera } from "lucide-react";
import { QuantityControl } from "./QuantityControl";

interface ProductFormProps {
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  onBarcodeChange: (barcode: string) => void;
  onNameChange: (name: string) => void;
  onBoxesChange: (boxes: number) => void;
  onUnitsPerBoxChange: (unitsPerBox: number) => void;
  onOpenScanner: () => void;
  onOpenCamera: () => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

export const ProductForm = ({
  barcode,
  name,
  boxes,
  unitsPerBox,
  photos,
  onBarcodeChange,
  onNameChange,
  onBoxesChange,
  onUnitsPerBoxChange,
  onOpenScanner,
  onOpenCamera,
  onSave,
  onCancel,
  isSaving
}: ProductFormProps) => {
  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>Product Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Barcode *</label>
          <div className="flex gap-2">
            <Input
              value={barcode}
              onChange={(e) => onBarcodeChange(e.target.value)}
              placeholder="Scan or enter barcode"
            />
            <Button onClick={onOpenScanner} variant="outline">
              <Scan className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Photos (required)</label>
          <Button onClick={onOpenCamera} variant="outline" className="w-full">
            <Camera className="w-4 h-4 mr-2" />
            Take Photo ({photos.length})
          </Button>
          {photos.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {photos.map((photo, index) => (
                <img
                  key={index}
                  src={photo}
                  alt={`Product photo ${index + 1}`}
                  className="w-full h-20 object-cover rounded border"
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Product Name (Optional)</label>
          <Input
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Enter product name (optional)"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <QuantityControl
            label="Boxes"
            value={boxes}
            onChange={onBoxesChange}
          />
          <QuantityControl
            label="Units/Box"
            value={unitsPerBox}
            onChange={onUnitsPerBoxChange}
          />
        </div>

        <div className="flex gap-3">
          <Button onClick={onSave} disabled={isSaving || photos.length === 0} className="flex-1">
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button onClick={onCancel} variant="outline" className="flex-1">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
